/**
 * Header services can be used to render items in the AppFrame header.
 *
 * AppFrame header is rendered at the top of the page, and it can contain various items like
 * widgets, icons, and components. The header items can be configured and managed through the header
 * services.
 *
 * The header services are basically an instance of collection services which provide methods to add,
 * remove, and manage header items in an ordered way.
 *
 * Basically AppFrame adds the following default items to the header:
 * - **Help menu (optional)**\
 *   Displays the help menu for the user.\
 *   Please see {@link "Services > Feature Services > Help Services" | Help Services} how to configure
 *   the online help menu.\
 *   Order: `2000`
 * - **Preferences menu:**\
 *   Displays the preferences menu for the user which allows the user to change
 *   the dark mode, language, and other preferences.\
 *   Please see {@link "Services > Feature Services > Preferences Menu Services" | Preferences Menu Services}
 *   for more details how to configure the preferences menu.\
 *   Order: `3000`
 * - **User info & user info menu:**\
 *   Displays the user information and provides a menu to access user-related actions
 *   like login or logout.\
 *   Please see {@link "Services > Feature Services > Profile Menu Services" | Profile Menu Services}
 *   for more details how to configure the user info menu.\
 *   Order: `4000`
 *
 * If an application module wants to add custom items to the header, it can use the `order` prop
 * for their added items, so they will be displayed in the correct order.
 *
 * See the {@link HeaderItemConfig} interface for more details how to configure the header items.
 *
 * @module Services > Feature Services > Header Services
 */
import { FC } from 'react';

import { CollectionConfig, CollectionItem, CollectionServices } from '../base/collections';

/**
 * Defines the type of header items that can be added to the AppFrame header.
 */
export enum HeaderItemType {
  /**
   * Defines the type of header item as a widget.
   *
   * See {@link HeaderWidgetItemConfig} for more details how to add a widget to the header.
   */
  WIDGET = 'WIDGET',

  /**
   * Defines the type of header item as an icon.
   *
   * See {@link HeaderIconItemConfig} for more details how to add an icon to the header .
   */
  ICON = 'ICON',
  /**
   * Defines the type of header item as a divider.
   *
   * See {@link HeaderDividerItemConfig} for more details how to add a divider to the header.
   */
  DIVIDER = 'DIVIDER',

  /**
   * Defines the type of header item as a component.
   *
   * See {@link HeaderComponentItemConfig} for more details how to add a component to the header.
   */
  COMPONENT = 'COMPONENT',
}

/**
 * Pre-defined header components types.
 *
 * This type is used internally by AppFrame.
 */
export enum HeaderComponent {
  UserInfo = 'UserInfo',
  PreferencesMenu = 'PreferencesMenu',
  HelpMenu = 'HelpMenu',
}

/**
 * The base interface for all header item configurations.
 *
 * It is extending the {@link CollectionConfig} interface to include the type and hidden
 * properties of the header item. Please take care that the following properties are
 * inherited from {@link CollectionConfig}:
 * - `id`: the unique identifier of the header item.
 * - `order`: the order of the header item in the header.
 */
export interface HeaderItemConfigBase extends CollectionConfig {
  /**
   * The type of the header item.
   */
  type: HeaderItemType;

  /**
   * If `true`, the header item will be hidden and not displayed in the header.
   *
   * @default false
   */
  hidden?: boolean;
}

/**
 * Defines the configuration for a divider item in the header.
 *
 * It doesn't have any specific properties beyond the base configuration, but the
 * `order` property can be used to define the order of the divider item in the header.
 *
 * @example
 * With the following code a divider item can be added in between the help menu and the
 * preferences menu:
 * ```ts
 * services.appFrame.header.addItem({
 *   type: HeaderItemType.DIVIDER,
 *   order: 2500
 * });
 * ```
 */
export interface HeaderDividerItemConfig extends HeaderItemConfigBase {
  /**
   * The type of the header item, which must be set to `HeaderItemType.DIVIDER`.
   */
  type: HeaderItemType.DIVIDER;
}

/**
 * Ddefines the configuration for an icon item in the header.
 *
 * @example
 * With the following code an icon item can be added to the header before the preferences
 * and online help menu item:
 * ```ts
 * services.appFrame.header.addItem({
 *   type: HeaderItemType.ICON,
 *   icon: 'FavoriteIcon',
 *   title: 'My favorites',
 *   order: 500,
 *   onClick: () => {
 *     console.log('My favorites icon clicked');
 *   },
 *   'data-testid': 'AppHeader-favoriteIcon'
 * });
 * ```
 */
export interface HeaderIconItemConfig extends HeaderItemConfigBase {
  /**
   * The type of the header item, which must be set to `HeaderItemType.ICON`.
   */
  type: HeaderItemType.ICON;

  /**
   * The icon to be displayed in the header item.
   *
   * This should be a valid icon name from the icon library used in the application or
   * an URL which points to an image link.
   */
  icon: string;

  /**
   * Callback function to be called when the icon is clicked.
   */
  onClick: () => void;

  /**
   * The title of the icon item, which will be displayed as a tooltip when the user hovers
   * over the icon.
   */
  title: string;

  /**
   * The data-testid attribute for the icon item for testing purposes.
   */
  'data-testid': string;
}

/**
 * Defines the configuration for a widget item in the header.
 *
 * To use widget rendering the application must have the widget registered in with the
 * {@link "AppFrame Services Interface".Services.registerWidget | services.registerWidget} method.
 * After the widget is registered, it can be used in the header configuration.
 *
 * @example
 * Register the widget in the application module:
 * ```ts
 * services.registerWidget({
 *   name: 'MyWidget',
 *   ...singleSpaReact({
 *     React,
 *     ReactDOM,
 *     ReactDOMClient,
 *     rootComponent: MyWidgetComponent
 *   })
 * });
 * ```
 *
 * Then the widget can be added to the header:
 * ```ts
 * services.appFrame.header.addItem({
 *   type: HeaderItemType.WIDGET,
 *   widget: 'MyWidget',
 *   order: 1000,
 * });
 * ```
 */
export interface HeaderWidgetItemConfig extends HeaderItemConfigBase {
  /**
   * The type of the header item, which must be set to `HeaderItemType.WIDGET`.
   */
  type: HeaderItemType.WIDGET;
  /**
   * The name of the widget to be displayed in the header item.
   */
  widget: string;
  /**
   * Optional CSS class name to be applied to the widget item in the header.
   */
  className?: string;
}

/**
 * Defines the configuration for a component item in the header.
 *
 * NOTE: component rendering is not the safest way to render components in the header. It is mostly
 * used in the AppFrame to render predefined components like user info, preferences menu, or help menu.
 *
 * It is recommended to use the widget rendering instead, which is more flexible and allows to use
 * the widget services to render the component in a microfrontend way.
 */
export interface HeaderComponentItemConfig extends HeaderItemConfigBase {
  type: HeaderItemType.COMPONENT;
  Component: FC | HeaderComponent;
}

/**
 * HeaderItemConfig is a union type that represents all possible configurations for header items.
 *
 * It includes configurations for different types of header items such as widgets, icons, components,
 * and dividers.
 */
export type HeaderItemConfig =
  | HeaderDividerItemConfig
  | HeaderIconItemConfig
  | HeaderComponentItemConfig
  | HeaderWidgetItemConfig;

/**
 * The base interface for all header items.
 *
 * It extends the {@link CollectionItem} interface to include the `id` and `order` properties
 * which are required for all header items.
 */
export interface HeaderItemBase extends CollectionItem, HeaderItemConfigBase {
  id: string;
  order: number;
}

type HeaderItemBaseWithoutType = Omit<HeaderItemBase, 'type'>;

/**
 * Defines the interface for a divider item in the header.
 *
 * It is the same as {@link HeaderDividerItemConfig} but it is always contains the `id` and `order`
 * properties which are assigned after the item configuration added by the `addItem` method.
 */
export interface HeaderDividerItem
  extends Omit<HeaderDividerItemConfig, keyof HeaderItemBaseWithoutType>,
    HeaderItemBaseWithoutType {}

/**
 * Defines the interface for an icon item in the header.
 *
 * It is the same as {@link HeaderIconItemConfig} but it is always contains the `id` and `order`
 * properties which are assigned after the item configuration added by the `addItem` method.
 */
export interface HeaderIconItem
  extends Omit<HeaderIconItemConfig, keyof HeaderItemBaseWithoutType>,
    HeaderItemBaseWithoutType {
  icon: string;
  onClick: () => void;
  title: string;
}

/**
 * Defines the interface for a widget item in the header.
 *
 * It is the same as {@link HeaderWidgetItemConfig} but it is always contains the `id` and `order`
 * properties which are assigned after the item configuration added by the `addItem` method.
 */
export interface HeaderWidgetItem
  extends Omit<HeaderWidgetItemConfig, keyof HeaderItemBaseWithoutType>,
    HeaderItemBaseWithoutType {
  widget: string;
  className?: string;
}

/**
 * Defines the interface for a widget item in the header.
 *
 * It is the same as {@link HeaderWidgetItemConfig} but it is always contains the `id` and `order`
 * properties which are assigned after the item configuration added by the `addItem` method.
 */
export interface HeaderComponentItem
  extends Omit<HeaderComponentItemConfig, keyof HeaderItemBaseWithoutType>,
    HeaderItemBaseWithoutType {}

/**
 * HeaderItem is a union type that represents all possible header items.
 *
 * The difference between `HeaderItem` and `HeaderItemConfig` is that `HeaderItem` is created by the
 * header services and it always contains the `id` and `order` properties.
 */
export type HeaderItem = HeaderWidgetItem | HeaderDividerItem | HeaderIconItem | HeaderComponentItem;

/**
 * Header services can be used to render items in the AppFrame header.
 */
export type HeaderServices = CollectionServices<HeaderItemConfig, HeaderItem>;

declare module 'types/services/appframe' {
  interface AppFrameServices {
    /**
     * Header services can be used to render items in the AppFrame header.
     */
    header: HeaderServices;
  }
}
